import React from 'react'

import { Link } from '@tanstack/react-router'
import { MessagesSquareIcon } from 'lucide-react'
import { AnimatePresence } from 'motion/react'

import { SortButton, type SortButtonProps } from '@/components/atoms/SortButton/SortButton.tsx'
import { Team, type TeamData, type TimeAgoFormat } from '@/components/molecules/Team/Team.tsx'
import { cn } from '@/lib/utils.ts'

export type TeamSidepanelTimeFormat = TimeAgoFormat

export interface TeamSidepanelLabels {
  team: string
}

export interface TeamSidepanelNavigation {
  sortBy: SortButtonProps['sortBy']
  filterByCarType?: 'automatic' | 'manual' | 'electric'
  selected?: string
}

export interface TeamSidepanelData {
  teams?: Array<Omit<TeamData, 'teamId'> & { id: string }>
  messages?: {
    regionId: string
    unreadConversationCount: number
    hasUnreadMessages: boolean
  }
}

export interface TeamSidepanelAction {
  onTeamSortByChanged: (value: 'asc' | 'desc') => void
  onTeamSelected: (teamId?: string) => void
}

export interface TeamSidepanelProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, TeamSidepanelTimeFormat, TeamSidepanelAction {
  labels: TeamSidepanelLabels
  data: TeamSidepanelData
  navigation: TeamSidepanelNavigation
}

export const TeamSidepanel = React.forwardRef<HTMLDivElement, TeamSidepanelProps>(
  ({ className, labels, data, navigation, timeAgoFormatter, onTeamSortByChanged, onTeamSelected, ...props }, ref) => (
    <div
      className={cn('relative flex h-screen min-h-screen w-36 flex-col items-center', className)}
      ref={ref}
      {...props}>
      <div className="pointer-events-none absolute top-0 left-0 z-50 h-full w-3 bg-gradient-to-r from-black/20 to-transparent" />
      <div className="z-10 flex h-fit w-36 flex-col items-center justify-stretch border-b border-solid border-b-gray-400 bg-gray-50">
        {data.messages && (
          <Link
            to="/dashboard/$regionId/messages"
            params={{ regionId: data.messages.regionId }}
            className="flex min-h-12 w-full items-center justify-center gap-3 border border-l-0 border-teal-400 bg-teal-100">
            <MessagesSquareIcon className="size-4 text-gray-900" />
            <p className="px-1 text-gray-900 select-none">{data.messages.unreadConversationCount}</p>
            <div
              className={cn('size-3 rounded-full', data.messages.hasUnreadMessages ? 'bg-teal-400' : 'bg-transparent')}
            />
          </Link>
        )}
        <div className="flex items-center gap-2 p-3">
          <p className="text-sm font-bold text-gray-900 select-none">{labels.team}</p>

          <SortButton sortBy={navigation.sortBy} onSortChanged={onTeamSortByChanged} />
        </div>
      </div>

      <div
        className="flex w-36 flex-col items-start overflow-x-hidden overflow-y-auto"
        style={{ height: 'calc(100vh - 103px' }}>
        <AnimatePresence initial={false}>
          {data.teams?.map(({ id, ...team }) => (
            <Team
              {...team}
              key={id}
              teamId={id}
              state={navigation.selected === id ? 'active' : 'default'}
              timeAgoFormatter={timeAgoFormatter}
              onClick={(event) => {
                onTeamSelected(id)
                event.stopPropagation()
              }}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
)
